import { PropsWithChildren, useState } from 'react';
import { captureException, captureMessage } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

const onError = ({ error, name, extra = {} }) => {
  if (error instanceof Error) {
    // we will capture using a CustomError class so we can more easily distinguish between different types of errors in sentry
    captureException(new CustomError(name, error), {
      extra: {
        extra: JSON.stringify(extra), // since the extra object can be anything, we will stringify it to make it easier to read in sentry
      },
    });
  } else {
    // this happens if we get a 400 response from the server since we are rejecting the promise with the response.json() object
    // so instead of an error object, we'll get the error message as a string
    captureMessage(name, {
      extra: {
        error,
        ...extra,
      },
    });
  }
};

const CustomQueryClientProvider = ({ children }: PropsWithChildren) => {
  /**
   * query client instance needs to be created by a state due to unstable client behavior on re-renders
   * https://tkdodo.eu/blog/react-query-fa-qs#2-the-queryclient-is-not-stable
   * https://tkdodo.eu/blog/use-state-for-one-time-initializations
   */
  const [queryClient] = useState(() => {
    return new QueryClient({
      queryCache: new QueryCache({
        // since useQuery doesn't have an onError callback, we'll handle logging to sentry here
        onError: (error, query) =>
          onError({
            error,
            name: query.queryKey[0],
            extra: query.queryKey[1], // generally the orderNumber or other variables we use in the query key to identify the query
          }),
      }),
      mutationCache: new MutationCache(), // we won't define the onError here and leave it up to the individual mutations to handle their own errors
    });
  });

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

export default CustomQueryClientProvider;
